import { useState, useEffect } from 'react'
import { getProjects, getEngineerBudgets, getSCurveHours } from '../api/client'
import { exportSCurve } from '../api/client'
import BillingReportModal from '../components/BillingReportModal'
import BillingRates from './BillingRates'
import ClientTables from './ClientTables'
import { useTranslation } from 'react-i18next'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LabelList,
} from 'recharts'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec']

function rowTotal(b) {
  return MONTH_KEYS.reduce((s, k) => s + (b[k] || 0), 0)
}

function colTotal(budgets, k) {
  return budgets.reduce((s, b) => s + (b[k] || 0), 0)
}

export default function SCurve() {
  const { t } = useTranslation('engineering')
  const HASH_TABS = { '#rates': 'rates', '#client-tables': 'client-tables' }
  const initialTab = (typeof window !== 'undefined' && HASH_TABS[window.location.hash]) || 'scurve'
  const [tab, setTab] = useState(initialTab)

  const selectTab = (next) => {
    setTab(next)
    if (typeof window !== 'undefined') {
      const hash = Object.entries(HASH_TABS).find(([, v]) => v === next)?.[0] || ''
      history.replaceState(null, '', window.location.pathname + window.location.search + hash)
    }
  }

  return (
    <div>
      <div className="px-8 pt-6">
        <div className="flex items-center gap-1 border-b border-gray-200">
          <button
            onClick={() => selectTab('scurve')}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === 'scurve' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t('sCurve.tabSCurve')}
          </button>
          <button
            onClick={() => selectTab('rates')}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === 'rates' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t('sCurve.tabRates')}
          </button>
          <button
            onClick={() => selectTab('client-tables')}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === 'client-tables' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t('sCurve.tabClientTables')}
          </button>
        </div>
      </div>
      {tab === 'scurve' && <SCurvePanel />}
      {tab === 'rates' && <BillingRates />}
      {tab === 'client-tables' && <ClientTables />}
    </div>
  )
}

function AccumulatedHoursChart() {
  const { t } = useTranslation('engineering')
  const [windowSel, setWindowSel] = useState('ytd')
  const [data, setData] = useState(null)

  useEffect(() => {
    let cancelled = false
    setData(null)
    getSCurveHours(windowSel).then(rows => {
      if (!cancelled) setData(rows)
    })
    return () => { cancelled = true }
  }, [windowSel])

  const WINDOWS = [
    ['week', t('sCurve.windowWeek')],
    ['month', t('sCurve.windowMonth')],
    ['ytd', t('sCurve.windowYTD')],
  ]

  const chartData = (data || []).map(d => ({
    name: d.code ? `${d.code} ${d.name}` : d.name,
    hours: d.hours,
    color: d.color,
  }))

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-700">{t('sCurve.chartTitle')}</h2>
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          {WINDOWS.map(([val, label]) => (
            <button
              key={val}
              onClick={() => setWindowSel(val)}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${windowSel === val ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {data === null ? (
        <div className="text-sm text-gray-400 py-10 text-center">{t('sCurve.chartLoading')}</div>
      ) : chartData.length === 0 ? (
        <div className="text-sm text-gray-400 py-10 text-center">{t('sCurve.chartEmpty')}</div>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 42)}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 40, top: 4, bottom: 4 }}>
            <XAxis type="number" tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => [`${v}h`, t('sCurve.chartTitle')]} />
            <Bar dataKey="hours" radius={[0, 4, 4, 0]}>
              {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              <LabelList dataKey="hours" position="right" formatter={(v) => `${v}h`} style={{ fontSize: 12, fill: '#374151' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

function SCurvePanel() {
  const { t } = useTranslation('engineering')
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [projects, setProjects] = useState([])
  const [expanded, setExpanded] = useState({})
  const [budgets, setBudgets] = useState({})
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [showBilling, setShowBilling] = useState(false)

  useEffect(() => {
    setLoading(true)
    getProjects().then(all => {
      setProjects(all.filter(p => p.is_s_curve && !p.is_purchase_only))
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    const openIds = Object.keys(expanded).filter(id => expanded[id])
    if (openIds.length === 0) return
    setBudgets({})
    openIds.forEach(id => {
      getEngineerBudgets(parseInt(id), year).then(data => {
        setBudgets(prev => ({ ...prev, [id]: data }))
      })
    })
  }, [year])

  const loadBudget = async (projectId) => {
    const data = await getEngineerBudgets(projectId, year)
    setBudgets(prev => ({ ...prev, [projectId]: data }))
  }

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = { ...prev, [id]: !prev[id] }
      if (next[id] && !budgets[id]) loadBudget(id)
      return next
    })
  }

  const handleDownload = () => {
    setDownloading(true)
    exportSCurve(year)
    setTimeout(() => setDownloading(false), 1500)
  }

  return (
    <div className="px-8 pb-8 pt-6">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">{t('sCurve.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('sCurve.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={e => setYear(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            {[2024, 2025, 2026, 2027, 2028].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={handleDownload}
            disabled={downloading || projects.length === 0}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {downloading ? t('sCurve.downloading') : t('sCurve.downloadReport')}
          </button>
          <button
            onClick={() => setShowBilling(true)}
            className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            {t('sCurve.billingReport')}
          </button>
        </div>
      </div>

      <AccumulatedHoursChart />

      {loading ? (
        <div className="text-gray-400 text-sm">{t('sCurve.loading')}</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm">{t('sCurve.noProjects')}</p>
          <p className="text-xs mt-1">{t('sCurve.noProjectsHint')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {projects.map(p => (
            <div
              key={p.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <button
                onClick={() => toggleExpand(p.id)}
                className="w-full flex items-center gap-4 px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.code && (
                      <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded shrink-0">{p.code}</span>
                    )}
                    <span className="font-semibold text-gray-900 text-sm">
                      {p.nickname || p.name}
                    </span>
                    {p.nickname && (
                      <span className="text-xs text-gray-400 truncate max-w-xs">{p.name}</span>
                    )}
                  </div>
                  {p.po_pm && (
                    <p className="text-xs text-gray-400 mt-0.5">{p.po_pm}</p>
                  )}
                </div>
                <span className="text-xs text-gray-400 shrink-0">
                  {expanded[p.id] ? t('sCurve.hide') : t('sCurve.showEngineers')}
                </span>
              </button>

              {expanded[p.id] && (
                <div className="border-t border-gray-100">
                  {!budgets[p.id] ? (
                    <div className="px-5 py-4 text-sm text-gray-400">{t('sCurve.loadingBudgets')}</div>
                  ) : budgets[p.id].length === 0 ? (
                    <div className="px-5 py-4 text-sm text-gray-400">
                      {t('sCurve.noEngineerBudgets', { year })}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-separate border-spacing-0">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="sticky left-0 bg-gray-50 border-b border-r border-gray-200 px-4 py-2.5 text-left text-xs font-semibold text-gray-600 min-w-44">{t('sCurve.engineerColumn')}</th>
                            {MONTHS.map(m => (
                              <th key={m} className="border-b border-gray-200 px-2 py-2.5 text-center text-xs font-semibold text-gray-600 min-w-14">{m}</th>
                            ))}
                            <th className="border-b border-gray-200 px-3 py-2.5 text-center text-xs font-semibold text-gray-600 min-w-16">{t('sCurve.totalColumn')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {budgets[p.id].map((b, i) => (
                            <tr key={b.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                              <td className={`sticky left-0 border-b border-r border-gray-100 px-4 py-2 text-sm font-medium text-gray-800 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                {b.engineer_name}
                              </td>
                              {MONTH_KEYS.map(k => (
                                <td key={k} className="border-b border-gray-100 px-2 py-2 text-center text-sm text-gray-700">
                                  {b[k] ? b[k] : <span className="text-gray-300">—</span>}
                                </td>
                              ))}
                              <td className="border-b border-gray-100 px-3 py-2 text-center text-sm font-bold text-gray-900">
                                {rowTotal(b)}
                              </td>
                            </tr>
                          ))}

                          {budgets[p.id].length > 1 && (
                            <tr className="bg-gray-50">
                              <td className="sticky left-0 bg-gray-50 border-t border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700">{t('sCurve.totalColumn')}</td>
                              {MONTH_KEYS.map(k => (
                                <td key={k} className="border-t border-gray-200 px-2 py-2 text-center text-xs font-semibold text-gray-700">
                                  {colTotal(budgets[p.id], k) || ''}
                                </td>
                              ))}
                              <td className="border-t border-gray-200 px-3 py-2 text-center text-xs font-bold text-gray-700">
                                {budgets[p.id].reduce((s, b) => s + rowTotal(b), 0)}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showBilling && <BillingReportModal onClose={() => setShowBilling(false)} />}
    </div>
  )
}
